import React, { useEffect, useState, useMemo } from 'react';
import { adminApi, getImageUrl } from '../../services/api';
import { ShoppingBag, ExternalLink, Eye, Pencil, FileText, X, Clock, AlertTriangle, History, Shield, UserCheck, Activity, RefreshCw, Search } from 'lucide-react';
import Swal from "../../utils/apparifySwal";
import { Button } from '../../components/ui';
import AdminTablePagination from './AdminTablePagination';

const PAGE_SIZE = 10;

const SUSPEND_REASONS = [
    { value: '', label: 'Select a reason…' },
    { value: 'Policy Violation', label: 'Policy Violation' },
    { value: 'Fraudulent Activity', label: 'Fraudulent Activity' },
    { value: 'Reported by Users', label: 'Reported by Users' },
    { value: 'Document Issues', label: 'Document Issues' },
    { value: 'Counterfeit Products', label: 'Counterfeit Products' },
    { value: 'Non-compliance', label: 'Non-compliance' },
    { value: 'Other', label: 'Other (write below)' },
];

const DURATION_PRESETS = [
    { value: '', label: 'Indefinite' },
    { value: '1', label: '1 day' },
    { value: '3', label: '3 days' },
    { value: '7', label: '7 days' },
    { value: '14', label: '14 days' },
    { value: '30', label: '30 days' },
    { value: '90', label: '90 days' },
    { value: 'custom', label: 'Custom date…' },
];

const AdminBusinesses = () => {
    const [businesses, setBusinesses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredBusinesses, setFilteredBusinesses] = useState([]);
    const [selectedBusiness, setSelectedBusiness] = useState(null);
    const [parsedDetails, setParsedDetails] = useState(null);
    const [moderationNote, setModerationNote] = useState('');
    const [suspendReason, setSuspendReason] = useState('');
    const [durationPreset, setDurationPreset] = useState('');
    const [customDate, setCustomDate] = useState('');
    const [moderating, setModerating] = useState(false);
    const [detailTab, setDetailTab] = useState('details');
    const [timeline, setTimeline] = useState([]);
    const [timelineLoading, setTimelineLoading] = useState(false);
    const [timelineFilter, setTimelineFilter] = useState('all');
    const [page, setPage] = useState(1);

    const fetchBusinesses = async () => {
        try {
            setLoading(true);
            const response = await adminApi.getBusinesses();
            // Adjust for response structure
            const list = response.data || response || [];
            setBusinesses(list);
            setFilteredBusinesses(list);
            setError(null);
        } catch (err) {
            console.error("Error fetching businesses:", err);
            setError("Failed to load businesses.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBusinesses();
    }, []);

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredBusinesses(businesses);
        } else {
            const term = searchTerm.toLowerCase();
            const filtered = businesses.filter(b => 
                (b.name && b.name.toLowerCase().includes(term)) ||
                (b.slug && b.slug.toLowerCase().includes(term)) ||
                (b.owner_name && b.owner_name.toLowerCase().includes(term)) ||
                (b.owner_email && b.owner_email.toLowerCase().includes(term))
            );
            setFilteredBusinesses(filtered);
        }
    }, [searchTerm, businesses]);

    useEffect(() => setPage(1), [searchTerm]);

    const totalPages = Math.max(1, Math.ceil(filteredBusinesses.length / PAGE_SIZE));
    const visibleBusinesses = useMemo(
        () => filteredBusinesses.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [filteredBusinesses, page],
    );

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const openDetailsModal = async (business) => {
        let detail = business;
        try {
            const resp = await adminApi.getBusinessDetail(business.id);
            detail = resp.data || resp || business;
        } catch (e) {
            console.error('Failed to fetch business detail, using list row fallback', e);
        }

        setSelectedBusiness(detail);
        try {
            const details = detail.description ? JSON.parse(detail.description) : {};
            
            let filesObj = {};
            if (details.files) {
                 if (Array.isArray(details.files)) {
                    details.files.forEach(f => {
                        filesObj[f.field] = f.filename;
                    });
                 } else {
                     filesObj = details.files;
                 }
            }
            
            let addressStr = 'N/A';
            if (details.address) {
                if (typeof details.address === 'object') {
                    const { line1, line2, city, province, postalCode } = details.address;
                    addressStr = [line1, line2, city, province, postalCode].filter(Boolean).join(', ');
                } else {
                    addressStr = details.address;
                }
            } else if (business.address) {
                 addressStr = business.address;
            }
    
            setParsedDetails({
                ...details,
                files: filesObj,
                address: addressStr
            });
        } catch (e) {
            console.error("Failed to parse business description", e);
            setParsedDetails({});
        }
    };

    const handleModeration = async (business, actionType) => {
        if (!business?.id) return;

        const actionLabel = actionType === 'SUSPEND' ? 'suspend' : 'unsuspend';

        // For suspensions, compose the reason from preset + freeform note
        let composedReason = moderationNote || null;
        let suspendUntil = null;

        if (actionType === 'SUSPEND') {
            const parts = [];
            if (suspendReason && suspendReason !== 'Other') parts.push(suspendReason);
            if (moderationNote.trim()) parts.push(moderationNote.trim());
            composedReason = parts.join(' — ') || null;

            // Compute suspendUntil from duration preset
            if (durationPreset === 'custom' && customDate) {
                suspendUntil = new Date(customDate).toISOString();
            } else if (durationPreset && durationPreset !== 'custom') {
                const d = new Date();
                d.setDate(d.getDate() + Number(durationPreset));
                suspendUntil = d.toISOString();
            }
            // else: indefinite (null)
        }

        const result = await Swal.fire({
            title: `${actionLabel.charAt(0).toUpperCase()}${actionLabel.slice(1)} business?`,
            text: `This will ${actionLabel} "${business.name}".${suspendUntil ? `\nUntil: ${new Date(suspendUntil).toLocaleDateString()}` : ''}`,
            icon: actionType === 'SUSPEND' ? 'warning' : 'question',
            showCancelButton: true,
            confirmButtonText: `Yes, ${actionLabel}`,
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            setModerating(true);
            await adminApi.moderateBusiness(business.id, actionType, composedReason, suspendUntil);
            await Swal.fire('Success', `Business ${actionLabel}ed successfully.`, 'success');
            setModerationNote('');
            setSuspendReason('');
            setDurationPreset('');
            setCustomDate('');
            closeDetailsModal();
            fetchBusinesses();
        } catch (err) {
            console.error(`Failed to ${actionLabel} business`, err);
            Swal.fire('Error', `Failed to ${actionLabel} business.`, 'error');
        } finally {
            setModerating(false);
        }
    };
    
    const closeDetailsModal = () => {
        setSelectedBusiness(null);
        setParsedDetails(null);
        setDetailTab('details');
        setTimeline([]);
        setTimelineFilter('all');
    };

    const fetchTimeline = async (businessId) => {
        try {
            setTimelineLoading(true);
            const resp = await adminApi.getBusinessTimeline(businessId);
            setTimeline(resp.data || resp || []);
        } catch (e) {
            console.error('Failed to fetch business timeline', e);
            setTimeline([]);
        } finally {
            setTimelineLoading(false);
        }
    };

    const LANE_CONFIG = {
        status_change: { label: 'Status Change', icon: Activity, color: 'blue', bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', dot: 'bg-blue-500' },
        moderation:    { label: 'Moderation', icon: Shield, color: 'orange', bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', dot: 'bg-orange-500' },
        admin_action:  { label: 'Admin Action', icon: UserCheck, color: 'purple', bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', dot: 'bg-purple-500' },
        appeal:        { label: 'Appeal', icon: FileText, color: 'emerald', bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-500' },
        member_activity: { label: 'Member Activity', icon: Activity, color: 'sky', bg: 'bg-sky-100 dark:bg-sky-900/30', text: 'text-sky-700 dark:text-sky-300', dot: 'bg-sky-500' },
    };

    const filteredTimeline = useMemo(() => {
        if (timelineFilter === 'all') return timeline;
        return timeline.filter(e => e.lane === timelineFilter);
    }, [timeline, timelineFilter]);

    const renderFileLink = (label, filename) => {
        if (!filename) return null;
        const fileUrl = getImageUrl(filename);
        return (
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg mb-2">
                <div className="flex items-center">
                    <FileText className="w-5 h-5 mr-2 text-blue-500" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{label}</span>
                </div>
                <a 
                    href={fileUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                    <Eye className="w-4 h-4 mr-1" /> View
                </a>
            </div>
        );
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'ACTIVE':
                return 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800';
            case 'PENDING':
                return 'bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800';
            case 'REJECTED':
                return 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800';
            case 'SUSPENDED':
                return 'bg-orange-100 text-orange-800 border border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800';
            default:
                return 'bg-gray-100 text-gray-800 border border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
        }
    };

    return (
        <div className="admin-data-panel admin-workspace-panel operations-surface overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-center gap-4">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                    Business Directory
                </h2>
                <div className="flex items-center gap-4 w-full sm:w-auto">
                    <div className="relative w-full sm:w-72">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" aria-hidden="true" />
                        <input
                            type="text"
                            placeholder="Search businesses..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="theme-input pl-9 pr-3"
                        />
                    </div>
                    <Button
                        onClick={fetchBusinesses}
                        icon={RefreshCw}
                        loading={loading}
                        aria-label="Refresh businesses"
                    >
                        Refresh
                    </Button>
                </div>
            </div>

            <div className="operations-table-wrap">
                {loading ? (
                    <div className="p-8 text-center">
                        <div className="inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-neutral-300 border-t-indigo-600 dark:border-neutral-700 dark:border-t-indigo-400"></div>
                        <p className="mt-2 text-gray-500 dark:text-gray-400">Loading businesses...</p>
                    </div>
                ) : error ? (
                    <div className="p-8 text-center text-red-500 dark:text-red-400">
                        {error}
                    </div>
                ) : filteredBusinesses.length === 0 ? (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                        {searchTerm ? 'No businesses match your search.' : 'No businesses found.'}
                    </div>
                ) : (
                    <table className="admin-data-table w-full text-left text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Business
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Owner
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Status
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Joined
                                </th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {visibleBusinesses.map((business) => (
                                <tr key={business.id} className="theme-table-row">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300">
                                                <ShoppingBag size={20} />
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                    {business.name}
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    /{business.slug}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900 dark:text-white">
                                            {business.owner_name || 'N/A'}
                                        </div>
                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                            {business.owner_email}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(business.status)}`}>
                                            {business.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                        {new Date(business.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => openDetailsModal(business)}
                                                className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                                                title="View Details"
                                            >
                                                <Pencil size={18} />
                                            </button>
                                            {business.status === 'ACTIVE' && (
                                                <a 
                                                    href={`/shop/${business.slug}`} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="theme-icon-button text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
                                                    title="Visit Shop"
                                                >
                                                    <ExternalLink size={18} />
                                                </a>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <AdminTablePagination page={page} totalPages={totalPages} totalItems={filteredBusinesses.length} pageSize={PAGE_SIZE} onPageChange={setPage} />

            {/* Details Modal */}
            {selectedBusiness && parsedDetails && (
                <div className="admin-modal-backdrop theme-modal-backdrop overflow-y-auto">
                    <div className="admin-action-modal theme-modal-panel max-w-3xl max-h-[90vh] flex flex-col">
                        {/* Header */}
                        <div className="admin-modal-header flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    {selectedBusiness.name}
                                </h3>
                                <span className={`mt-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(selectedBusiness.status)}`}>
                                    {selectedBusiness.status}
                                </span>
                            </div>
                            <button 
                                onClick={closeDetailsModal}
                                className="theme-icon-button"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        {/* Tab Bar */}
                        <div className="admin-modal-tabs flex border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                            <button
                                onClick={() => setDetailTab('details')}
                                className={`theme-tab ${
                                    detailTab === 'details'
                                        ? 'theme-tab-active'
                                        : 'theme-tab-idle'
                                }`}
                            >
                                <Eye size={14} className="inline mr-1.5 -mt-0.5" />
                                Details
                            </button>
                            <button
                                onClick={() => { setDetailTab('timeline'); if (!timeline.length) fetchTimeline(selectedBusiness.id); }}
                                className={`theme-tab ${
                                    detailTab === 'timeline'
                                        ? 'theme-tab-active'
                                        : 'theme-tab-idle'
                                }`}
                            >
                                <History size={14} className="inline mr-1.5 -mt-0.5" />
                                Audit Timeline
                            </button>
                        </div>

                        {/* Scrollable content area */}
                        <div className="admin-modal-scroll-region flex-1 overflow-y-auto">

                        {/* ── Details Tab ── */}
                        {detailTab === 'details' && (
                        <div className="admin-modal-body p-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                                        Business Info
                                    </h4>
                                    <div className="space-y-3">
                                        <p className="text-sm text-gray-800 dark:text-gray-200">
                                            <span className="font-medium">Type:</span> {parsedDetails.businessType || 'N/A'}
                                        </p>
                                        <p className="text-sm text-gray-800 dark:text-gray-200">
                                            <span className="font-medium">Tax ID (TIN):</span> {parsedDetails.tin || 'N/A'}
                                        </p>
                                        <p className="text-sm text-gray-800 dark:text-gray-200">
                                            <span className="font-medium">Address:</span> {parsedDetails.address || 'N/A'}
                                        </p>
                                    </div>
                                </div>
                                
                                <div>
                                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                                        Owner Info
                                    </h4>
                                    <div className="space-y-3">
                                        <p className="text-sm text-gray-800 dark:text-gray-200">
                                            <span className="font-medium">Name:</span> {selectedBusiness.owner_name}
                                        </p>
                                        <p className="text-sm text-gray-800 dark:text-gray-200">
                                            <span className="font-medium">Email:</span> {selectedBusiness.owner_email}
                                        </p>
                                        <p className="text-sm text-gray-800 dark:text-gray-200">
                                            <span className="font-medium">Phone:</span> {parsedDetails.phone || 'N/A'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                                    Submitted Documents
                                </h4>
                                <div className="grid grid-cols-1 gap-4">
                                    {selectedBusiness.documents?.length > 0 ? (
                                        selectedBusiness.documents.map((doc) => (
                                            <div key={doc.id || `${doc.field_name}-${doc.stored_filename || doc.file_key}`}>
                                                {renderFileLink(doc.field_name || 'Document', doc.file_key || doc.stored_filename)}
                                            </div>
                                        ))
                                    ) : (
                                        <>
                                            {renderFileLink('Business Permit', parsedDetails.files?.permit)}
                                            {renderFileLink('Valid ID', parsedDetails.files?.validId)}
                                            {(!parsedDetails.files?.permit && !parsedDetails.files?.validId) && (
                                                <p className="text-sm text-gray-500 italic">No documents attached.</p>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                                    Moderation
                                </h4>

                                {/* Suspend-specific fields */}
                                {selectedBusiness.status !== 'SUSPENDED' && (
                                    <div className="space-y-3 mb-3">
                                        {/* Preset reason */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                                                <AlertTriangle size={12} className="inline mr-1" />
                                                Suspension Reason
                                            </label>
                                            <select
                                                value={suspendReason}
                                                onChange={(e) => setSuspendReason(e.target.value)}
                                                className="theme-select rounded-lg"
                                            >
                                                {SUSPEND_REASONS.map((r) => (
                                                    <option key={r.value} value={r.value}>{r.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Duration */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                                                <Clock size={12} className="inline mr-1" />
                                                Duration
                                            </label>
                                            <select
                                                value={durationPreset}
                                                onChange={(e) => { setDurationPreset(e.target.value); setCustomDate(''); }}
                                                className="theme-select rounded-lg"
                                            >
                                                {DURATION_PRESETS.map((d) => (
                                                    <option key={d.value} value={d.value}>{d.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Custom date picker */}
                                        {durationPreset === 'custom' && (
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                                                    Suspend Until
                                                </label>
                                                <input
                                                    type="datetime-local"
                                                    value={customDate}
                                                    onChange={(e) => setCustomDate(e.target.value)}
                                                    min={new Date().toISOString().slice(0, 16)}
                                                    className="theme-input rounded-lg"
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                <textarea
                                    value={moderationNote}
                                    onChange={(e) => setModerationNote(e.target.value)}
                                    placeholder={selectedBusiness.status !== 'SUSPENDED' ? "Additional notes (optional — appended to reason)" : "Internal moderation note or reason"}
                                    className="theme-textarea min-h-[90px]"
                                />
                                <div className="mt-3 flex items-center gap-2">
                                    <button
                                        onClick={() => handleModeration(selectedBusiness, 'SUSPEND')}
                                        disabled={moderating || selectedBusiness.status === 'SUSPENDED'}
                                        className="theme-btn-warning px-3"
                                    >
                                        Suspend
                                    </button>
                                    <button
                                        onClick={() => handleModeration(selectedBusiness, 'UNSUSPEND')}
                                        disabled={moderating || selectedBusiness.status !== 'SUSPENDED'}
                                        className="theme-btn-success px-3"
                                    >
                                        Unsuspend
                                    </button>
                                </div>
                            </div>
                        </div>
                        )}

                        {/* ── Timeline Tab ── */}
                        {detailTab === 'timeline' && (
                        <div className="admin-modal-body p-6 space-y-4">
                            {/* Lane filter pills */}
                            <div className="flex flex-wrap gap-2">
                                {[
                                    { key: 'all', label: 'All Events' },
                                    ...Object.entries(LANE_CONFIG).map(([key, cfg]) => ({ key, label: cfg.label })),
                                ].map(f => (
                                    <button
                                        key={f.key}
                                        onClick={() => setTimelineFilter(f.key)}
                                        className={`theme-filter-pill ${
                                            timelineFilter === f.key
                                                ? 'theme-filter-pill-active'
                                                : ''
                                        }`}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>

                            {timelineLoading ? (
                                <div className="py-12 text-center">
                                    <div className="inline-block h-6 w-6 animate-spin rounded-full border-b-2 border-t-2 border-neutral-300 border-t-indigo-600 dark:border-neutral-700 dark:border-t-indigo-400"></div>
                                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading timeline...</p>
                                </div>
                            ) : filteredTimeline.length === 0 ? (
                                <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                                    No events found{timelineFilter !== 'all' ? ' for this filter' : ''}.
                                </div>
                            ) : (
                                <div className="relative">
                                    {/* Vertical line */}
                                    <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-gray-200 dark:bg-gray-700" />

                                    <div className="space-y-0">
                                        {filteredTimeline.map((event, idx) => {
                                            const lane = LANE_CONFIG[event.lane] || LANE_CONFIG.admin_action;
                                            const LaneIcon = lane.icon;
                                            let meta = null;
                                            try { if (event.metadata) meta = typeof event.metadata === 'string' ? JSON.parse(event.metadata) : event.metadata; } catch {}

                                            return (
                                                <div key={`${event.lane}-${idx}`} className="relative pl-10 py-3 group">
                                                    {/* Dot */}
                                                    <div className={`absolute left-2.5 top-4.5 w-3 h-3 rounded-full ring-2 ring-white dark:ring-gray-800 ${lane.dot}`} />

                                                    <div className="flex items-start gap-3">
                                                        <div className={`flex-shrink-0 p-1.5 rounded-md ${lane.bg}`}>
                                                            <LaneIcon size={14} className={lane.text} />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full ${lane.bg} ${lane.text}`}>
                                                                    {lane.label}
                                                                </span>
                                                                <span className="text-xs font-mono text-gray-400 dark:text-gray-500">
                                                                    {event.event_type}
                                                                </span>
                                                            </div>
                                                            <p className="mt-1 text-sm text-gray-800 dark:text-gray-200 break-words">
                                                                {event.summary || '—'}
                                                            </p>
                                                            <div className="mt-1 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                                                                <span>{event.actor_name || event.actor_email || 'System'}</span>
                                                                <span>{new Date(event.created_at).toLocaleString()}</span>
                                                            </div>
                                                            {meta && (
                                                                <details className="mt-1.5">
                                                                    <summary className="text-[11px] text-gray-400 dark:text-gray-500 cursor-pointer hover:text-gray-600 dark:hover:text-gray-300">
                                                                        metadata
                                                                    </summary>
                                                                    <pre className="mt-1 p-2 text-[11px] bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 overflow-x-auto max-h-32 text-gray-600 dark:text-gray-400">
                                                                        {JSON.stringify(meta, null, 2)}
                                                                    </pre>
                                                                </details>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {filteredTimeline.length > 0 && (
                                <p className="text-[11px] text-gray-400 dark:text-gray-500 text-center pt-2">
                                    Showing {filteredTimeline.length} event{filteredTimeline.length !== 1 ? 's' : ''} (newest first, max 200)
                                </p>
                            )}
                        </div>
                        )}

                        </div>{/* end scrollable content area */}
                        
                        <div className="admin-modal-footer theme-panel-footer flex flex-shrink-0 justify-end p-4">
                            <button
                                onClick={closeDetailsModal}
                                className="theme-btn-neutral"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminBusinesses;
